import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';

const InvestorDashboard = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});
  const [sortKey, setSortKey] = useState('officialName');
  const [sortDir, setSortDir] = useState('asc');
  const [search, setSearch] = useState('');
  const [fundingFilter, setFundingFilter] = useState('all'); // all | with | without

  useEffect(() => {
    (async () => {
      try {
        const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/investor-accounts/companies`);
        const data = await response.json();
        if (response.ok) {
          setCompanies(data.companies || []);
        } else {
          setError(data.error || 'Failed to load companies');
        }
      } catch (err) {
        setError('Failed to load companies');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = companies.filter(c => c.name);
    if (q) {
      list = list.filter(c =>
        (c.officialName || c.name || '').toLowerCase().includes(q) ||
        (c.overview || '').toLowerCase().includes(q)
      );
    }
    if (fundingFilter === 'with') list = list.filter(c => c.hasFunding);
    if (fundingFilter === 'without') list = list.filter(c => !c.hasFunding);
    return list.sort((a, b) => {
      const av = (a[sortKey] || '').toString().toLowerCase();
      const bv = (b[sortKey] || '').toString().toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [companies, search, fundingFilter, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  const sortIndicator = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading companies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Investor Dashboard</h1>
          <p className="mt-2 text-gray-600">View company portfolios and investor materials.</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <div className="mb-4 flex flex-wrap gap-3 items-center">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search companies..."
            className="border border-gray-300 rounded px-3 py-2 text-sm flex-1 min-w-[200px]"
          />
          <select
            value={fundingFilter}
            onChange={(e) => setFundingFilter(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 text-sm"
          >
            <option value="all">All companies</option>
            <option value="with">With funding</option>
            <option value="without">Without funding</option>
          </select>
        </div>

        {filtered.length === 0 && !error ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <p className="text-gray-500">No companies match your filters.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 w-8"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => toggleSort('officialName')}>
                    Company{sortIndicator('officialName')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => toggleSort('hasFunding')}>
                    Funding{sortIndicator('hasFunding')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filtered.map((company) => {
                  const isOpen = !!expanded[company.name];
                  return (
                    <React.Fragment key={company.name}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => setExpanded(prev => ({ ...prev, [company.name]: !prev[company.name] }))}
                            className="text-gray-500 hover:text-gray-700"
                            title="Show company overview"
                          >
                            {isOpen ? '▼' : '▶'}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                          <div className="flex items-center gap-3">
                            {company.logoUrl && (
                              <img src={company.logoUrl} alt="" className="h-8 w-8 object-contain rounded" />
                            )}
                            <span>{company.officialName || company.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {company.hasFunding ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Has funding
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              No funding
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => navigate(`/investor-portal/${encodeURIComponent(company.name)}`)}
                            className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                          >
                            View Portal →
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-gray-50">
                          <td colSpan={4} className="px-6 py-4 text-sm text-gray-700">
                            {company.overview ? (
                              <p className="whitespace-pre-wrap">{company.overview}</p>
                            ) : (
                              <p className="text-gray-400 italic">No company overview provided.</p>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvestorDashboard;
